import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { LibraryManager, ResourceType } from 'libs.library_manager';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { DEFAULT_CUBE_DATA_SOURCE } from '../../../data_views/constants/data_view.constants';
/**
 * Batches a single Library lookup for the given CUBE_CONFIG resource ids and returns the subset
 * that are on the new (starrocks) infra — mirrors the `data_source` check used to gate the
 * "New Infrastructure" toggle in data_views (use_data_views.hook.ts).
 */
export const useReportInfraStatus = (cubeConfigIds) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const { entityId } = useContext(AuthContext);
    const uniqueIds = useMemo(() => [...new Set(cubeConfigIds.filter((id) => !!id))].sort(), [cubeConfigIds]);
    const { data: newInfraIds = new Set(), isLoading } = useQuery({
        queryKey: ['data_reports', 'cube_config_infra_status', uniqueIds, entityId],
        queryFn: async () => {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClient });
            // entityId is required here — every other CUBE_CONFIG list lookup in this codebase
            // (use_data_views, use_dataset_version_check, …) scopes by entityId; without it the
            // Library ACL filter comes back empty and every report looks like it's on legacy infra.
            const { data: resources } = await libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                id: uniqueIds,
                entityId,
            });
            return new Set(resources
                .filter((resource) => resource.data?.data_source === DEFAULT_CUBE_DATA_SOURCE)
                .map((resource) => resource.id));
        },
        enabled: uniqueIds.length > 0 && !!entityId,
    });
    return { newInfraIds, isLoading };
};
